export type ThemeMode = "light" | "dark";

export type ThemeId =
  | "pure-white"
  | "warm-latte"
  | "vercel-light"
  | "github-light"
  | "clarity-light"
  | "linear-light"
  | "solarized-light"
  | "midnight-teal"
  | "obsidian-black"
  | "dracula-purple"
  | "nord-frost"
  | "cobalt-blue"
  | "emerald-matrix"
  | "sunset-amber"
  | "crimson-night";

export interface ThemeVariables {
  "--surface-root": string;
  "--surface-sidebar": string;
  "--surface-header": string;
  "--surface-card": string;
  "--surface-card-hover": string;
  "--surface-elevated": string;
  "--surface-input": string;
  "--surface-muted": string;
  "--surface-overlay": string;
  "--surface-disabled": string;

  "--border-default": string;
  "--border-subtle": string;
  "--border-accent": string;
  "--border-input": string;
  "--border-disabled": string;

  "--text-primary": string;
  "--text-secondary": string;
  "--text-muted": string;
  "--text-disabled": string;

  "--accent-primary": string;
  "--accent-secondary": string;
  "--accent-hover": string;
  "--accent-glow": string;
  "--accent-text": string;
  "--accent-subtle": string;

  "--status-success": string;
  "--status-success-bg": string;
  "--status-success-border": string;
  "--status-success-text": string;

  "--status-warning": string;
  "--status-warning-bg": string;
  "--status-warning-border": string;
  "--status-warning-text": string;

  "--status-danger": string;
  "--status-danger-bg": string;
  "--status-danger-border": string;
  "--status-danger-text": string;

  "--status-info": string;
  "--status-info-bg": string;
  "--status-info-border": string;
  "--status-info-text": string;

  "--focus-ring": string;
}

export interface ThemeConfig {
  id: ThemeId;
  name: string;
  category: string;
  mode: ThemeMode;
  description: string;
  icon: string;
  variables: ThemeVariables;
  swatches: {
    background: string;
    card: string;
    accent: string;
    text: string;
  };
}

export const THEMES: ThemeConfig[] = [
  // 1. PURE PROFESSIONAL WHITE (Light Theme)
  {
    id: "pure-white",
    name: "Pure Professional White",
    category: "Light",
    mode: "light",
    description: "Clean slate white with crisp borders and high-contrast typography.",
    icon: "☀️",
    variables: {
      "--surface-root": "#f8fafc",
      "--surface-sidebar": "#ffffff",
      "--surface-header": "#ffffff",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f1f5f9",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#f1f5f9",
      "--surface-overlay": "rgba(15, 23, 42, 0.5)",
      "--surface-disabled": "#f1f5f9",

      "--border-default": "#e2e8f0",
      "--border-subtle": "#f1f5f9",
      "--border-accent": "rgba(13, 148, 136, 0.4)",
      "--border-input": "#cbd5e1",
      "--border-disabled": "#e2e8f0",

      "--text-primary": "#0f172a",
      "--text-secondary": "#475569",
      "--text-muted": "#64748b",
      "--text-disabled": "#94a3b8",

      "--accent-primary": "#0d9488",
      "--accent-secondary": "#06b6d4",
      "--accent-hover": "#0f766e",
      "--accent-glow": "rgba(13, 148, 136, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(13, 148, 136, 0.08)",

      "--status-success": "#059669",
      "--status-success-bg": "#ecfdf5",
      "--status-success-border": "#a7f3d0",
      "--status-success-text": "#065f46",

      "--status-warning": "#d97706",
      "--status-warning-bg": "#fffbeb",
      "--status-warning-border": "#fde68a",
      "--status-warning-text": "#92400e",

      "--status-danger": "#e11d48",
      "--status-danger-bg": "#fff1f2",
      "--status-danger-border": "#fecdd3",
      "--status-danger-text": "#9f1239",

      "--status-info": "#0284c7",
      "--status-info-bg": "#f0f9ff",
      "--status-info-border": "#bae6fd",
      "--status-info-text": "#075985",

      "--focus-ring": "#0d9488",
    },
    swatches: {
      background: "#f8fafc",
      card: "#ffffff",
      accent: "#0d9488",
      text: "#0f172a",
    },
  },

  // 2. WARM CREAM / LATTE (Light Theme)
  {
    id: "warm-latte",
    name: "Warm Cream / Latte",
    category: "Light",
    mode: "light",
    description: "Premium sandstone warmth with espresso and amber accents.",
    icon: "☕",
    variables: {
      "--surface-root": "#fcfaf7",
      "--surface-sidebar": "#f5f0e8",
      "--surface-header": "#f5f0e8",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f5f0e8",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#eee7dc",
      "--surface-overlay": "rgba(41, 37, 36, 0.5)",
      "--surface-disabled": "#eee7dc",

      "--border-default": "#e5ded4",
      "--border-subtle": "#f0eae1",
      "--border-accent": "rgba(217, 119, 6, 0.4)",
      "--border-input": "#d7cec2",
      "--border-disabled": "#e5ded4",

      "--text-primary": "#292524",
      "--text-secondary": "#57534e",
      "--text-muted": "#78716c",
      "--text-disabled": "#a8a29e",

      "--accent-primary": "#d97706",
      "--accent-secondary": "#b45309",
      "--accent-hover": "#b45309",
      "--accent-glow": "rgba(217, 119, 6, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(217, 119, 6, 0.08)",

      "--status-success": "#059669",
      "--status-success-bg": "#ecfdf5",
      "--status-success-border": "#a7f3d0",
      "--status-success-text": "#065f46",

      "--status-warning": "#b45309",
      "--status-warning-bg": "#fffbeb",
      "--status-warning-border": "#fde68a",
      "--status-warning-text": "#78350f",

      "--status-danger": "#e11d48",
      "--status-danger-bg": "#fff1f2",
      "--status-danger-border": "#fecdd3",
      "--status-danger-text": "#9f1239",

      "--status-info": "#0284c7",
      "--status-info-bg": "#f0f9ff",
      "--status-info-border": "#bae6fd",
      "--status-info-text": "#075985",

      "--focus-ring": "#d97706",
    },
    swatches: {
      background: "#fcfaf7",
      card: "#ffffff",
      accent: "#d97706",
      text: "#292524",
    },
  },

  // 3. VERCEL CLEAN LIGHT (Light Theme)
  {
    id: "vercel-light",
    name: "Vercel Clean Light",
    category: "Light",
    mode: "light",
    description: "Ultra-clean monochrome with Vercel electric blue accent and pure white surfaces.",
    icon: "▲",
    variables: {
      "--surface-root": "#fafafa",
      "--surface-sidebar": "#ffffff",
      "--surface-header": "#ffffff",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f4f4f5",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#f4f4f5",
      "--surface-overlay": "rgba(0, 0, 0, 0.45)",
      "--surface-disabled": "#f4f4f5",

      "--border-default": "#eaeaea",
      "--border-subtle": "#f2f2f2",
      "--border-accent": "rgba(0, 112, 243, 0.4)",
      "--border-input": "#d4d4d8",
      "--border-disabled": "#eaeaea",

      "--text-primary": "#000000",
      "--text-secondary": "#444444",
      "--text-muted": "#666666",
      "--text-disabled": "#999999",

      "--accent-primary": "#0070f3",
      "--accent-secondary": "#0051a2",
      "--accent-hover": "#0051a2",
      "--accent-glow": "rgba(0, 112, 243, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(0, 112, 243, 0.08)",

      "--status-success": "#059669",
      "--status-success-bg": "#ecfdf5",
      "--status-success-border": "#a7f3d0",
      "--status-success-text": "#065f46",

      "--status-warning": "#d97706",
      "--status-warning-bg": "#fffbeb",
      "--status-warning-border": "#fde68a",
      "--status-warning-text": "#92400e",

      "--status-danger": "#ee0000",
      "--status-danger-bg": "#fff1f2",
      "--status-danger-border": "#fecdd3",
      "--status-danger-text": "#9f1239",

      "--status-info": "#0070f3",
      "--status-info-bg": "#f0f9ff",
      "--status-info-border": "#bae6fd",
      "--status-info-text": "#075985",

      "--focus-ring": "#0070f3",
    },
    swatches: {
      background: "#fafafa",
      card: "#ffffff",
      accent: "#0070f3",
      text: "#000000",
    },
  },

  // 4. GITHUB PRIMER LIGHT (Light Theme)
  {
    id: "github-light",
    name: "GitHub Primer Light",
    category: "Light",
    mode: "light",
    description: "Official GitHub Primer palette with cool gray canvas and classic blue accents.",
    icon: "🐙",
    variables: {
      "--surface-root": "#ffffff",
      "--surface-sidebar": "#f6f8fa",
      "--surface-header": "#f6f8fa",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f6f8fa",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#f6f8fa",
      "--surface-overlay": "rgba(31, 35, 40, 0.5)",
      "--surface-disabled": "#eff2f5",

      "--border-default": "#d0d7de",
      "--border-subtle": "#eaeef2",
      "--border-accent": "rgba(9, 105, 218, 0.4)",
      "--border-input": "#bcc5cf",
      "--border-disabled": "#d0d7de",

      "--text-primary": "#1f2328",
      "--text-secondary": "#57606a",
      "--text-muted": "#6e7781",
      "--text-disabled": "#8c959f",

      "--accent-primary": "#0969da",
      "--accent-secondary": "#0550ae",
      "--accent-hover": "#0550ae",
      "--accent-glow": "rgba(9, 105, 218, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(9, 105, 218, 0.08)",

      "--status-success": "#1a7f37",
      "--status-success-bg": "#dafbe1",
      "--status-success-border": "#8ce99a",
      "--status-success-text": "#116329",

      "--status-warning": "#9a6700",
      "--status-warning-bg": "#fff8c5",
      "--status-warning-border": "#f1e05a",
      "--status-warning-text": "#633c01",

      "--status-danger": "#cf222e",
      "--status-danger-bg": "#ffebe9",
      "--status-danger-border": "#ffc1c0",
      "--status-danger-text": "#82071e",

      "--status-info": "#0969da",
      "--status-info-bg": "#ddf4ff",
      "--status-info-border": "#80ccff",
      "--status-info-text": "#0550ae",

      "--focus-ring": "#0969da",
    },
    swatches: {
      background: "#ffffff",
      card: "#ffffff",
      accent: "#0969da",
      text: "#1f2328",
    },
  },

  // 5. VMWARE CLARITY LIGHT (Light Theme)
  {
    id: "clarity-light",
    name: "VMware Clarity Light",
    category: "Light",
    mode: "light",
    description: "Enterprise virtualization design system with steel tones and VMware cobalt blue.",
    icon: "🏢",
    variables: {
      "--surface-root": "#fafafa",
      "--surface-sidebar": "#f0f3f6",
      "--surface-header": "#ffffff",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f0f3f6",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#e9edf0",
      "--surface-overlay": "rgba(0, 37, 56, 0.5)",
      "--surface-disabled": "#e9edf0",

      "--border-default": "#d9e1e7",
      "--border-subtle": "#e8edf2",
      "--border-accent": "rgba(0, 101, 171, 0.4)",
      "--border-input": "#c2ccd6",
      "--border-disabled": "#d9e1e7",

      "--text-primary": "#000000",
      "--text-secondary": "#333333",
      "--text-muted": "#565656",
      "--text-disabled": "#9b9b9b",

      "--accent-primary": "#0065ab",
      "--accent-secondary": "#004a7c",
      "--accent-hover": "#004a7c",
      "--accent-glow": "rgba(0, 101, 171, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(0, 101, 171, 0.08)",

      "--status-success": "#217a00",
      "--status-success-bg": "#e8f5e9",
      "--status-success-border": "#a5d6a7",
      "--status-success-text": "#1b5e20",

      "--status-warning": "#c96500",
      "--status-warning-bg": "#fff3e0",
      "--status-warning-border": "#ffcc80",
      "--status-warning-text": "#e65100",

      "--status-danger": "#c21d00",
      "--status-danger-bg": "#ffebee",
      "--status-danger-border": "#ef9a9a",
      "--status-danger-text": "#b71c1c",

      "--status-info": "#0065ab",
      "--status-info-bg": "#e1f5fe",
      "--status-info-border": "#81d4fa",
      "--status-info-text": "#01579b",

      "--focus-ring": "#0065ab",
    },
    swatches: {
      background: "#fafafa",
      card: "#ffffff",
      accent: "#0065ab",
      text: "#000000",
    },
  },

  // 6. LINEAR MODERN LIGHT (Light Theme)
  {
    id: "linear-light",
    name: "Linear Modern Light",
    category: "Light",
    mode: "light",
    description: "Modern product engineering style with micro-borders and Linear blurple accents.",
    icon: "📐",
    variables: {
      "--surface-root": "#f7f8f9",
      "--surface-sidebar": "#ffffff",
      "--surface-header": "#ffffff",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#f0f2f5",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#f0f2f5",
      "--surface-overlay": "rgba(15, 23, 42, 0.5)",
      "--surface-disabled": "#f0f2f5",

      "--border-default": "#e1e4e8",
      "--border-subtle": "#eff1f4",
      "--border-accent": "rgba(94, 106, 210, 0.4)",
      "--border-input": "#cbcfd6",
      "--border-disabled": "#e1e4e8",

      "--text-primary": "#111827",
      "--text-secondary": "#4b5563",
      "--text-muted": "#6b7280",
      "--text-disabled": "#9ca3af",

      "--accent-primary": "#5e6ad2",
      "--accent-secondary": "#4b55c2",
      "--accent-hover": "#4b55c2",
      "--accent-glow": "rgba(94, 106, 210, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(94, 106, 210, 0.08)",

      "--status-success": "#10b981",
      "--status-success-bg": "#ecfdf5",
      "--status-success-border": "#a7f3d0",
      "--status-success-text": "#065f46",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "#fffbeb",
      "--status-warning-border": "#fde68a",
      "--status-warning-text": "#92400e",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "#fef2f2",
      "--status-danger-border": "#fecaca",
      "--status-danger-text": "#991b1b",

      "--status-info": "#3b82f6",
      "--status-info-bg": "#eff6ff",
      "--status-info-border": "#bfdbfe",
      "--status-info-text": "#1e40af",

      "--focus-ring": "#5e6ad2",
    },
    swatches: {
      background: "#f7f8f9",
      card: "#ffffff",
      accent: "#5e6ad2",
      text: "#111827",
    },
  },

  // 7. SOLARIZED DESIGNER LIGHT (Light Theme)
  {
    id: "solarized-light",
    name: "Solarized Designer Light",
    category: "Light",
    mode: "light",
    description: "Ethan Schoonover's precision solarized palette with warm base and cyan accents.",
    icon: "🌅",
    variables: {
      "--surface-root": "#fdf6e3",
      "--surface-sidebar": "#eee8d5",
      "--surface-header": "#eee8d5",
      "--surface-card": "#ffffff",
      "--surface-card-hover": "#eee8d5",
      "--surface-elevated": "#ffffff",
      "--surface-input": "#ffffff",
      "--surface-muted": "#eee8d5",
      "--surface-overlay": "rgba(7, 54, 66, 0.5)",
      "--surface-disabled": "#eee8d5",

      "--border-default": "#dfd7c2",
      "--border-subtle": "#ede5cf",
      "--border-accent": "rgba(38, 139, 210, 0.4)",
      "--border-input": "#cec5af",
      "--border-disabled": "#dfd7c2",

      "--text-primary": "#073642",
      "--text-secondary": "#586e75",
      "--text-muted": "#657b83",
      "--text-disabled": "#93a1a1",

      "--accent-primary": "#268bd2",
      "--accent-secondary": "#2aa198",
      "--accent-hover": "#1e6ea8",
      "--accent-glow": "rgba(38, 139, 210, 0.15)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(38, 139, 210, 0.08)",

      "--status-success": "#859900",
      "--status-success-bg": "#f4f8e6",
      "--status-success-border": "#c7d87b",
      "--status-success-text": "#4d5900",

      "--status-warning": "#b58900",
      "--status-warning-bg": "#fdf6e2",
      "--status-warning-border": "#e6ce7b",
      "--status-warning-text": "#715600",

      "--status-danger": "#dc322f",
      "--status-danger-bg": "#fbeeed",
      "--status-danger-border": "#f1a5a3",
      "--status-danger-text": "#961c1a",

      "--status-info": "#2aa198",
      "--status-info-bg": "#eaf6f5",
      "--status-info-border": "#98d5d0",
      "--status-info-text": "#18605a",

      "--focus-ring": "#268bd2",
    },
    swatches: {
      background: "#fdf6e3",
      card: "#ffffff",
      accent: "#268bd2",
      text: "#073642",
    },
  },

  // 8. MIDNIGHT TEAL & CYAN (Dark Cyber Default)
  {
    id: "midnight-teal",
    name: "Midnight Teal & Cyan",
    category: "Dark",
    mode: "dark",
    description: "Default Cyber-Hosting theme with rich navy depths and vibrant teal glows.",
    icon: "🌌",
    variables: {
      "--surface-root": "#070b14",
      "--surface-sidebar": "#0a0f1d",
      "--surface-header": "#0b101f",
      "--surface-card": "#0d1322",
      "--surface-card-hover": "#121a30",
      "--surface-elevated": "#162038",
      "--surface-input": "#090e1a",
      "--surface-muted": "#141d33",
      "--surface-overlay": "rgba(3, 7, 18, 0.75)",
      "--surface-disabled": "#111827",

      "--border-default": "#1e293b",
      "--border-subtle": "#162033",
      "--border-accent": "rgba(20, 184, 166, 0.35)",
      "--border-input": "#1e293b",
      "--border-disabled": "#1f2937",

      "--text-primary": "#f8fafc",
      "--text-secondary": "#94a3b8",
      "--text-muted": "#64748b",
      "--text-disabled": "#475569",

      "--accent-primary": "#14b8a6",
      "--accent-secondary": "#06b6d4",
      "--accent-hover": "#2dd4bf",
      "--accent-glow": "rgba(20, 184, 166, 0.25)",
      "--accent-text": "#022c22",
      "--accent-subtle": "rgba(20, 184, 166, 0.12)",

      "--status-success": "#10b981",
      "--status-success-bg": "rgba(16, 185, 129, 0.12)",
      "--status-success-border": "rgba(16, 185, 129, 0.3)",
      "--status-success-text": "#34d399",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "rgba(245, 158, 11, 0.12)",
      "--status-warning-border": "rgba(245, 158, 11, 0.3)",
      "--status-warning-text": "#fbbf24",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "rgba(239, 68, 68, 0.12)",
      "--status-danger-border": "rgba(239, 68, 68, 0.3)",
      "--status-danger-text": "#f87171",

      "--status-info": "#06b6d4",
      "--status-info-bg": "rgba(6, 182, 212, 0.12)",
      "--status-info-border": "rgba(6, 182, 212, 0.3)",
      "--status-info-text": "#38bdf8",

      "--focus-ring": "#14b8a6",
    },
    swatches: {
      background: "#070b14",
      card: "#0d1322",
      accent: "#14b8a6",
      text: "#f8fafc",
    },
  },

  // 4. OBSIDIAN PURE OLED (Dark OLED Theme)
  {
    id: "obsidian-black",
    name: "Obsidian Pure OLED",
    category: "Dark",
    mode: "dark",
    description: "True OLED black with vibrant emerald neon accents and maximum contrast.",
    icon: "🖤",
    variables: {
      "--surface-root": "#000000",
      "--surface-sidebar": "#050505",
      "--surface-header": "#080808",
      "--surface-card": "#0c0c0c",
      "--surface-card-hover": "#141414",
      "--surface-elevated": "#171717",
      "--surface-input": "#050505",
      "--surface-muted": "#171717",
      "--surface-overlay": "rgba(0, 0, 0, 0.85)",
      "--surface-disabled": "#121212",

      "--border-default": "#1f1f1f",
      "--border-subtle": "#141414",
      "--border-accent": "rgba(34, 197, 94, 0.4)",
      "--border-input": "#262626",
      "--border-disabled": "#1f1f1f",

      "--text-primary": "#ffffff",
      "--text-secondary": "#a1a1aa",
      "--text-muted": "#71717a",
      "--text-disabled": "#52525b",

      "--accent-primary": "#22c55e",
      "--accent-secondary": "#10b981",
      "--accent-hover": "#4ade80",
      "--accent-glow": "rgba(34, 197, 94, 0.3)",
      "--accent-text": "#000000",
      "--accent-subtle": "rgba(34, 197, 94, 0.12)",

      "--status-success": "#22c55e",
      "--status-success-bg": "rgba(34, 197, 94, 0.12)",
      "--status-success-border": "rgba(34, 197, 94, 0.3)",
      "--status-success-text": "#4ade80",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "rgba(245, 158, 11, 0.12)",
      "--status-warning-border": "rgba(245, 158, 11, 0.3)",
      "--status-warning-text": "#fbbf24",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "rgba(239, 68, 68, 0.12)",
      "--status-danger-border": "rgba(239, 68, 68, 0.3)",
      "--status-danger-text": "#f87171",

      "--status-info": "#06b6d4",
      "--status-info-bg": "rgba(6, 182, 212, 0.12)",
      "--status-info-border": "rgba(6, 182, 212, 0.3)",
      "--status-info-text": "#38bdf8",

      "--focus-ring": "#22c55e",
    },
    swatches: {
      background: "#000000",
      card: "#0c0c0c",
      accent: "#22c55e",
      text: "#ffffff",
    },
  },

  // 5. DRACULA CYBER PURPLE (Dark Theme)
  {
    id: "dracula-purple",
    name: "Dracula Cyber Purple",
    category: "Dark",
    mode: "dark",
    description: "Deep violet depths with glowing fuchsia accents and sharp contrast.",
    icon: "🔮",
    variables: {
      "--surface-root": "#13111c",
      "--surface-sidebar": "#171422",
      "--surface-header": "#181524",
      "--surface-card": "#1e1a2d",
      "--surface-card-hover": "#28233c",
      "--surface-elevated": "#2d2744",
      "--surface-input": "#120f1b",
      "--surface-muted": "#28233c",
      "--surface-overlay": "rgba(10, 8, 16, 0.8)",
      "--surface-disabled": "#1e1a2d",

      "--border-default": "#382f54",
      "--border-subtle": "#28223d",
      "--border-accent": "rgba(192, 132, 252, 0.4)",
      "--border-input": "#382f54",
      "--border-disabled": "#28223d",

      "--text-primary": "#f3e8ff",
      "--text-secondary": "#d8b4fe",
      "--text-muted": "#a855f7",
      "--text-disabled": "#6b21a8",

      "--accent-primary": "#a855f7",
      "--accent-secondary": "#d946ef",
      "--accent-hover": "#c084fc",
      "--accent-glow": "rgba(168, 85, 247, 0.3)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(168, 85, 247, 0.14)",

      "--status-success": "#34d399",
      "--status-success-bg": "rgba(52, 211, 153, 0.12)",
      "--status-success-border": "rgba(52, 211, 153, 0.3)",
      "--status-success-text": "#6ee7b7",

      "--status-warning": "#fbbf24",
      "--status-warning-bg": "rgba(251, 191, 36, 0.12)",
      "--status-warning-border": "rgba(251, 191, 36, 0.3)",
      "--status-warning-text": "#fde68a",

      "--status-danger": "#f87171",
      "--status-danger-bg": "rgba(248, 113, 113, 0.12)",
      "--status-danger-border": "rgba(248, 113, 113, 0.3)",
      "--status-danger-text": "#fca5a5",

      "--status-info": "#38bdf8",
      "--status-info-bg": "rgba(56, 189, 248, 0.12)",
      "--status-info-border": "rgba(56, 189, 248, 0.3)",
      "--status-info-text": "#7dd3fc",

      "--focus-ring": "#a855f7",
    },
    swatches: {
      background: "#13111c",
      card: "#1e1a2d",
      accent: "#a855f7",
      text: "#f3e8ff",
    },
  },

  // 6. NORD ARCTIC FROST (Dark Theme)
  {
    id: "nord-frost",
    name: "Nord Arctic Frost",
    category: "Dark",
    mode: "dark",
    description: "Arctic cold blue with polar slate cards and ice-cyan accents.",
    icon: "❄️",
    variables: {
      "--surface-root": "#242933",
      "--surface-sidebar": "#1e222a",
      "--surface-header": "#212530",
      "--surface-card": "#2e3440",
      "--surface-card-hover": "#3b4252",
      "--surface-elevated": "#434c5e",
      "--surface-input": "#1a1e24",
      "--surface-muted": "#3b4252",
      "--surface-overlay": "rgba(26, 30, 36, 0.8)",
      "--surface-disabled": "#2e3440",

      "--border-default": "#434c5e",
      "--border-subtle": "#3b4252",
      "--border-accent": "rgba(136, 192, 208, 0.4)",
      "--border-input": "#434c5e",
      "--border-disabled": "#3b4252",

      "--text-primary": "#eceff4",
      "--text-secondary": "#d8dee9",
      "--text-muted": "#9fb3c8",
      "--text-disabled": "#616e88",

      "--accent-primary": "#88c0d0",
      "--accent-secondary": "#81a1c1",
      "--accent-hover": "#8fbcbb",
      "--accent-glow": "rgba(136, 192, 208, 0.3)",
      "--accent-text": "#1e222a",
      "--accent-subtle": "rgba(136, 192, 208, 0.14)",

      "--status-success": "#a3be8c",
      "--status-success-bg": "rgba(163, 190, 140, 0.15)",
      "--status-success-border": "rgba(163, 190, 140, 0.35)",
      "--status-success-text": "#a3be8c",

      "--status-warning": "#ebcb8b",
      "--status-warning-bg": "rgba(235, 203, 139, 0.15)",
      "--status-warning-border": "rgba(235, 203, 139, 0.35)",
      "--status-warning-text": "#ebcb8b",

      "--status-danger": "#bf616a",
      "--status-danger-bg": "rgba(191, 97, 106, 0.15)",
      "--status-danger-border": "rgba(191, 97, 106, 0.35)",
      "--status-danger-text": "#d08770",

      "--status-info": "#88c0d0",
      "--status-info-bg": "rgba(136, 192, 208, 0.15)",
      "--status-info-border": "rgba(136, 192, 208, 0.35)",
      "--status-info-text": "#88c0d0",

      "--focus-ring": "#88c0d0",
    },
    swatches: {
      background: "#242933",
      card: "#2e3440",
      accent: "#88c0d0",
      text: "#eceff4",
    },
  },

  // 7. COBALT ENTERPRISE (Dark Corporate Theme)
  {
    id: "cobalt-blue",
    name: "Cobalt Enterprise",
    category: "Dark",
    mode: "dark",
    description: "Deep corporate navy blue with sapphire accents and professional slate cards.",
    icon: "💼",
    variables: {
      "--surface-root": "#080e1e",
      "--surface-sidebar": "#0b1429",
      "--surface-header": "#0d1730",
      "--surface-card": "#111d40",
      "--surface-card-hover": "#172754",
      "--surface-elevated": "#1c2f66",
      "--surface-input": "#070d1c",
      "--surface-muted": "#172754",
      "--surface-overlay": "rgba(4, 7, 15, 0.8)",
      "--surface-disabled": "#0f1833",

      "--border-default": "#1d3269",
      "--border-subtle": "#14234a",
      "--border-accent": "rgba(59, 130, 246, 0.4)",
      "--border-input": "#1d3269",
      "--border-disabled": "#14234a",

      "--text-primary": "#eff6ff",
      "--text-secondary": "#93c5fd",
      "--text-muted": "#60a5fa",
      "--text-disabled": "#3b82f6",

      "--accent-primary": "#3b82f6",
      "--accent-secondary": "#60a5fa",
      "--accent-hover": "#60a5fa",
      "--accent-glow": "rgba(59, 130, 246, 0.3)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(59, 130, 246, 0.14)",

      "--status-success": "#10b981",
      "--status-success-bg": "rgba(16, 185, 129, 0.12)",
      "--status-success-border": "rgba(16, 185, 129, 0.3)",
      "--status-success-text": "#34d399",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "rgba(245, 158, 11, 0.12)",
      "--status-warning-border": "rgba(245, 158, 11, 0.3)",
      "--status-warning-text": "#fbbf24",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "rgba(239, 68, 68, 0.12)",
      "--status-danger-border": "rgba(239, 68, 68, 0.3)",
      "--status-danger-text": "#f87171",

      "--status-info": "#3b82f6",
      "--status-info-bg": "rgba(59, 130, 246, 0.12)",
      "--status-info-border": "rgba(59, 130, 246, 0.3)",
      "--status-info-text": "#60a5fa",

      "--focus-ring": "#3b82f6",
    },
    swatches: {
      background: "#080e1e",
      card: "#111d40",
      accent: "#3b82f6",
      text: "#eff6ff",
    },
  },

  // 8. EMERALD MATRIX (Dark Theme)
  {
    id: "emerald-matrix",
    name: "Emerald Matrix",
    category: "Dark",
    mode: "dark",
    description: "Deep pine and matrix green on luxury dark tech surfaces.",
    icon: "🌲",
    variables: {
      "--surface-root": "#04120e",
      "--surface-sidebar": "#061a14",
      "--surface-header": "#071f18",
      "--surface-card": "#0b2920",
      "--surface-card-hover": "#0f382c",
      "--surface-elevated": "#134738",
      "--surface-input": "#030d0a",
      "--surface-muted": "#0f382c",
      "--surface-overlay": "rgba(2, 9, 7, 0.8)",
      "--surface-disabled": "#09221b",

      "--border-default": "#134e3f",
      "--border-subtle": "#0d382d",
      "--border-accent": "rgba(16, 185, 129, 0.4)",
      "--border-input": "#134e3f",
      "--border-disabled": "#0d382d",

      "--text-primary": "#ecfdf5",
      "--text-secondary": "#6ee7b7",
      "--text-muted": "#34d399",
      "--text-disabled": "#059669",

      "--accent-primary": "#10b981",
      "--accent-secondary": "#14b8a6",
      "--accent-hover": "#34d399",
      "--accent-glow": "rgba(16, 185, 129, 0.3)",
      "--accent-text": "#022c22",
      "--accent-subtle": "rgba(16, 185, 129, 0.14)",

      "--status-success": "#10b981",
      "--status-success-bg": "rgba(16, 185, 129, 0.12)",
      "--status-success-border": "rgba(16, 185, 129, 0.3)",
      "--status-success-text": "#34d399",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "rgba(245, 158, 11, 0.12)",
      "--status-warning-border": "rgba(245, 158, 11, 0.3)",
      "--status-warning-text": "#fbbf24",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "rgba(239, 68, 68, 0.12)",
      "--status-danger-border": "rgba(239, 68, 68, 0.3)",
      "--status-danger-text": "#f87171",

      "--status-info": "#14b8a6",
      "--status-info-bg": "rgba(20, 184, 166, 0.12)",
      "--status-info-border": "rgba(20, 184, 166, 0.3)",
      "--status-info-text": "#2dd4bf",

      "--focus-ring": "#10b981",
    },
    swatches: {
      background: "#04120e",
      card: "#0b2920",
      accent: "#10b981",
      text: "#ecfdf5",
    },
  },

  // 9. SUNSET AMBER & GOLD (Dark Warm Theme)
  {
    id: "sunset-amber",
    name: "Sunset Amber & Gold",
    category: "Dark",
    mode: "dark",
    description: "Warm charcoal surfaces with golden amber highlights and rich copper glow.",
    icon: "🌅",
    variables: {
      "--surface-root": "#140e08",
      "--surface-sidebar": "#1a120b",
      "--surface-header": "#1e150d",
      "--surface-card": "#271b10",
      "--surface-card-hover": "#362516",
      "--surface-elevated": "#422d1b",
      "--surface-input": "#100b06",
      "--surface-muted": "#362516",
      "--surface-overlay": "rgba(10, 7, 4, 0.8)",
      "--surface-disabled": "#22170e",

      "--border-default": "#4a301c",
      "--border-subtle": "#332113",
      "--border-accent": "rgba(245, 158, 11, 0.4)",
      "--border-input": "#4a301c",
      "--border-disabled": "#332113",

      "--text-primary": "#fef3c7",
      "--text-secondary": "#fcd34d",
      "--text-muted": "#d97706",
      "--text-disabled": "#78350f",

      "--accent-primary": "#f59e0b",
      "--accent-secondary": "#ea580c",
      "--accent-hover": "#fbbf24",
      "--accent-glow": "rgba(245, 158, 11, 0.3)",
      "--accent-text": "#1a120b",
      "--accent-subtle": "rgba(245, 158, 11, 0.14)",

      "--status-success": "#10b981",
      "--status-success-bg": "rgba(16, 185, 129, 0.12)",
      "--status-success-border": "rgba(16, 185, 129, 0.3)",
      "--status-success-text": "#34d399",

      "--status-warning": "#f59e0b",
      "--status-warning-bg": "rgba(245, 158, 11, 0.12)",
      "--status-warning-border": "rgba(245, 158, 11, 0.3)",
      "--status-warning-text": "#fcd34d",

      "--status-danger": "#ef4444",
      "--status-danger-bg": "rgba(239, 68, 68, 0.12)",
      "--status-danger-border": "rgba(239, 68, 68, 0.3)",
      "--status-danger-text": "#f87171",

      "--status-info": "#06b6d4",
      "--status-info-bg": "rgba(6, 182, 212, 0.12)",
      "--status-info-border": "rgba(6, 182, 212, 0.3)",
      "--status-info-text": "#38bdf8",

      "--focus-ring": "#f59e0b",
    },
    swatches: {
      background: "#140e08",
      card: "#271b10",
      accent: "#f59e0b",
      text: "#fef3c7",
    },
  },

  // 10. CRIMSON NIGHT / RUBY (Dark Red Theme)
  {
    id: "crimson-night",
    name: "Crimson Night / Ruby",
    category: "Dark",
    mode: "dark",
    description: "Deep ruby charcoal with velvet slate panels and scarlet accents.",
    icon: "🍷",
    variables: {
      "--surface-root": "#12070a",
      "--surface-sidebar": "#170a0e",
      "--surface-header": "#1a0b10",
      "--surface-card": "#240f16",
      "--surface-card-hover": "#331520",
      "--surface-elevated": "#3e1926",
      "--surface-input": "#0f0508",
      "--surface-muted": "#331520",
      "--surface-overlay": "rgba(9, 3, 5, 0.8)",
      "--surface-disabled": "#1e0c12",

      "--border-default": "#4a1b2a",
      "--border-subtle": "#33121d",
      "--border-accent": "rgba(244, 63, 94, 0.4)",
      "--border-input": "#4a1b2a",
      "--border-disabled": "#33121d",

      "--text-primary": "#ffe4e6",
      "--text-secondary": "#fda4af",
      "--text-muted": "#f43f5e",
      "--text-disabled": "#881337",

      "--accent-primary": "#e11d48",
      "--accent-secondary": "#be123c",
      "--accent-hover": "#fb7185",
      "--accent-glow": "rgba(225, 29, 72, 0.3)",
      "--accent-text": "#ffffff",
      "--accent-subtle": "rgba(225, 29, 72, 0.14)",

      "--status-success": "#10b981",
      "--status-success-bg": "rgba(16, 185, 129, 0.12)",
      "--status-success-border": "rgba(16, 185, 129, 0.3)",
      "--status-success-text": "#34d399",

      "--status-warning": "#fbbf24",
      "--status-warning-bg": "rgba(251, 191, 36, 0.12)",
      "--status-warning-border": "rgba(251, 191, 36, 0.3)",
      "--status-warning-text": "#fde68a",

      "--status-danger": "#f43f5e",
      "--status-danger-bg": "rgba(244, 63, 94, 0.12)",
      "--status-danger-border": "rgba(244, 63, 94, 0.3)",
      "--status-danger-text": "#fda4af",

      "--status-info": "#38bdf8",
      "--status-info-bg": "rgba(56, 189, 248, 0.12)",
      "--status-info-border": "rgba(56, 189, 248, 0.3)",
      "--status-info-text": "#7dd3fc",

      "--focus-ring": "#e11d48",
    },
    swatches: {
      background: "#12070a",
      card: "#240f16",
      accent: "#e11d48",
      text: "#ffe4e6",
    },
  },
];

export const DEFAULT_THEME: ThemeId = "midnight-teal";

export const LIGHT_THEME_IDS: ThemeId[] = THEMES.filter((t) => t.mode === "light").map((t) => t.id);

export function generateThemeCss(): string {
  const defaultTheme = getThemeById(DEFAULT_THEME);
  let css = "/* ==========================================================================\n   THEME SYSTEM - GENERATED CSS VARIABLES (Source of truth: themes.ts)\n   ========================================================================== */\n:root {\n";
  for (const [key, value] of Object.entries(defaultTheme.variables)) {
    css += `  ${key}: ${value};\n`;
  }
  css += "}\n\n";

  for (const theme of THEMES) {
    css += `html[data-theme="${theme.id}"] {\n`;
    for (const [key, value] of Object.entries(theme.variables)) {
      css += `  ${key}: ${value};\n`;
    }
    css += "}\n\n";
  }

  return css;
}

export const THEME_CSS: string = generateThemeCss();

export function getThemeById(id: string | null | undefined): ThemeConfig {
  const found = THEMES.find((t) => t.id === id);
  return found || THEMES.find((t) => t.id === DEFAULT_THEME)!;
}

